import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useInstallPrompt, usePushNotifications } from '../hooks/usePWA';
import { Download, Bell, Share, Plus, Check, Smartphone, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const InstallPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { canInstall, isInstalled, isIOS, promptInstall } = useInstallPrompt();
  const { permission, requestPermission, loading } = usePushNotifications();

  const handleInstall = async () => {
    const installed = await promptInstall();
    if (installed) {
      toast.success(language === 'fr' ? 'Application installée!' : 'App installed!');
    }
  };

  const handleEnableNotifications = async () => {
    const success = await requestPermission();
    if (success) {
      toast.success(language === 'fr' ? 'Notifications activées!' : 'Notifications enabled!');
    } else {
      toast.error(language === 'fr' ? 'Notifications refusées' : 'Notifications denied');
    }
  };

  const content = {
    fr: {
      title: 'Installer HandyVerse Pro',
      subtitle: 'Accédez rapidement à votre tableau de bord depuis votre écran d\'accueil',
      alreadyInstalled: 'Application déjà installée!',
      installBtn: 'Installer maintenant',
      iosTitle: 'Comment installer sur iPhone/iPad',
      iosStep1: 'Appuyez sur le bouton Partager',
      iosStep2: 'Faites défiler et appuyez sur "Sur l\'écran d\'accueil"',
      iosStep3: 'Appuyez sur "Ajouter" pour confirmer',
      androidTitle: 'Installation',
      notificationsTitle: 'Notifications push',
      notificationsDesc: 'Recevez des alertes instantanées pour les nouvelles demandes de réservation',
      enableNotifications: 'Activer les notifications',
      notificationsEnabled: 'Notifications activées',
      notificationsDenied: 'Notifications refusées',
      features: [
        'Accès instantané depuis l\'écran d\'accueil',
        'Fonctionne hors connexion',
        'Notifications push en temps réel',
        'Expérience native fluide'
      ],
      goToDashboard: 'Aller au tableau de bord'
    },
    en: {
      title: 'Install HandyVerse Pro',
      subtitle: 'Quick access to your dashboard from your home screen',
      alreadyInstalled: 'App already installed!',
      installBtn: 'Install now',
      iosTitle: 'How to install on iPhone/iPad',
      iosStep1: 'Tap the Share button',
      iosStep2: 'Scroll and tap "Add to Home Screen"',
      iosStep3: 'Tap "Add" to confirm',
      androidTitle: 'Installation',
      notificationsTitle: 'Push Notifications',
      notificationsDesc: 'Get instant alerts for new booking requests',
      enableNotifications: 'Enable notifications',
      notificationsEnabled: 'Notifications enabled',
      notificationsDenied: 'Notifications denied',
      features: [
        'Instant access from home screen',
        'Works offline',
        'Real-time push notifications',
        'Smooth native experience'
      ],
      goToDashboard: 'Go to dashboard'
    }
  };

  const t = content[language] || content.fr;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 py-12 px-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/30">
            <Smartphone className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">{t.title}</h1>
          <p className="text-slate-400">{t.subtitle}</p>
        </div>

        {/* Installation Card */}
        <Card className="mb-6 bg-slate-800/50 border-slate-700">
          <CardContent className="pt-6">
            {isInstalled ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Check className="w-8 h-8 text-green-400" />
                </div>
                <p className="text-green-400 font-medium">{t.alreadyInstalled}</p>
              </div>
            ) : isIOS ? (
              <div>
                <h3 className="font-semibold text-white mb-4">{t.iosTitle}</h3>
                <ol className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="w-8 h-8 rounded-full bg-orange-500/20 text-orange-400 flex items-center justify-center shrink-0 font-bold">1</span>
                    <div className="flex items-center gap-2 text-slate-300 pt-1">
                      {t.iosStep1}
                      <Share className="w-5 h-5 text-blue-400" />
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-8 h-8 rounded-full bg-orange-500/20 text-orange-400 flex items-center justify-center shrink-0 font-bold">2</span>
                    <div className="flex items-center gap-2 text-slate-300 pt-1">
                      {t.iosStep2}
                      <Plus className="w-5 h-5 text-blue-400" />
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-8 h-8 rounded-full bg-orange-500/20 text-orange-400 flex items-center justify-center shrink-0 font-bold">3</span>
                    <span className="text-slate-300 pt-1">{t.iosStep3}</span>
                  </li>
                </ol>
              </div>
            ) : canInstall ? (
              <div className="text-center">
                <h3 className="font-semibold text-white mb-4">{t.androidTitle}</h3>
                <Button
                  onClick={handleInstall}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-full py-6"
                >
                  <Download className="w-5 h-5 mr-2" />
                  {t.installBtn}
                </Button>
              </div>
            ) : (
              <div className="text-center text-slate-400 py-4">
                <p>{language === 'fr' 
                  ? 'L\'installation n\'est pas disponible dans ce navigateur'
                  : 'Installation not available in this browser'
                }</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notifications Card */}
        <Card className="mb-6 bg-slate-800/50 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center shrink-0">
                <Bell className="w-6 h-6 text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-white mb-1">{t.notificationsTitle}</h3>
                <p className="text-sm text-slate-400 mb-3">{t.notificationsDesc}</p>
                
                {permission === 'granted' ? (
                  <div className="flex items-center gap-2 text-green-400">
                    <Check className="w-4 h-4" />
                    <span className="text-sm">{t.notificationsEnabled}</span>
                  </div>
                ) : permission === 'denied' ? (
                  <div className="text-sm text-red-400">{t.notificationsDenied}</div>
                ) : (
                  <Button
                    onClick={handleEnableNotifications}
                    disabled={loading}
                    variant="outline"
                    className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 rounded-full"
                    size="sm"
                  >
                    {loading ? '...' : t.enableNotifications}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Features List */}
        <div className="mb-8">
          <ul className="space-y-3">
            {t.features.map((feature, index) => (
              <li key={index} className="flex items-center gap-3 text-slate-300">
                <Check className="w-5 h-5 text-green-400 shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <Button
          onClick={() => navigate('/pro/dashboard')}
          className="w-full bg-white text-slate-900 hover:bg-slate-100 rounded-full py-6 font-semibold"
        >
          {t.goToDashboard}
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default InstallPage;
